import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Volume2, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { surahAPI, verseAPI, recitationAPI } from '../services/api';
import AudioPlayer from '../components/AudioPlayer';
import SEOHead from '../components/SEOHead';
import CompactAudioPlayer from '../components/CompactAudioPlayer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const getFileUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  // Handle both /uploads/images/ and /uploads/ paths
  if (url.startsWith('/uploads/')) {
    const fileUrl = `${BACKEND_URL}/api/files/${url.replace('/uploads/', '')}`;
    console.log('[getFileUrl] Input:', url, '→ Output:', fileUrl);
    return fileUrl;
  }
  return `${BACKEND_URL}${url}`;
};

const QuranReader = () => {
  const [searchParams] = useSearchParams();
  const recitationIdFromUrl = searchParams.get('recitation') || null;
  const autoplayFromUrl = searchParams.get('autoplay') === 'true';
  
  // Add custom animation styles
  React.useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes live-pulse {
        0%, 100% {
          opacity: 1;
          transform: scale(1);
          box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
        }
        50% {
          opacity: 0.8;
          transform: scale(1.1);
          box-shadow: 0 0 0 4px rgba(239, 68, 68, 0);
        }
      }
      @keyframes slide-up {
        from {
          opacity: 0;
          transform: translateY(100px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }
      .animate-slide-up {
        animation: slide-up 0.4s ease-out;
      }
    `;
    document.head.appendChild(style);
    return () => {
      // Safe DOM removal - check if element still exists
      if (style.parentNode) {
        document.head.removeChild(style);
      }
    };
  }, []);
  const navigate = useNavigate();
  const surahId = searchParams.get('surah') || null;
  const [surahs, setSurahs] = useState([]);
  const [currentSurah, setCurrentSurah] = useState(null);
  const [currentVerses, setCurrentVerses] = useState([]);
  const [showArabic, setShowArabic] = useState(true);
  const [showTurkish, setShowTurkish] = useState(true);
  const [loading, setLoading] = useState(true);
  const [isReciterDialogOpen, setIsReciterDialogOpen] = useState(false);
  const [recitations, setRecitations] = useState([]);
  const [selectedRecitation, setSelectedRecitation] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [siteTexts, setSiteTexts] = useState({});
  const [currentAudioTime, setCurrentAudioTime] = useState(0);
  const [highlightedVerseIndex, setHighlightedVerseIndex] = useState(null);
  const [showNextSurahModal, setShowNextSurahModal] = useState(false);
  const [nextSurahCountdown, setNextSurahCountdown] = useState(7);
  const [nextSurahInfo, setNextSurahInfo] = useState(null);
  const [reciterChangeAlert, setReciterChangeAlert] = useState(null);

  useEffect(() => {
    loadSurahs();
    loadSiteTexts();
  }, []);

  const loadSiteTexts = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/site-texts`);
      const texts = await response.json();
      const textsMap = {};
      texts.forEach(text => {
        textsMap[text.key] = text.value;
      });
      setSiteTexts(textsMap);
    } catch (error) {
      console.error('Error loading site texts:', error);
    }
  };

  const getText = (key, defaultValue = '') => {
    return siteTexts[key] || defaultValue;
  };

  useEffect(() => {
    if (surahs.length > 0) {
      const selectedId = surahId || surahs[0].id;
      loadSurahData(selectedId);
    }
  }, [surahId, surahs]);

  const loadSurahs = async () => {
    try {
      const response = await surahAPI.getAll();
      setSurahs(response.data);
    } catch (error) {
      console.error('Error loading surahs:', error);
    }
  };

  const loadSurahData = async (identifier) => {
    setLoading(true);
    
    // Reset auto-next modal state when loading new surah
    setShowNextSurahModal(false);
    setNextSurahCountdown(7);
    setNextSurahInfo(null);
    
    try {
      console.log('[QuranReader] Loading surah:', identifier);
      const [surahRes, versesRes, recitationsRes] = await Promise.all([
        surahAPI.getOne(identifier), // Backend now accepts slug or ID
        verseAPI.getBySurah(identifier),
        recitationAPI.getBySurah(identifier).catch(() => ({ data: [] }))
      ]);
      console.log('[QuranReader] Surah loaded:', surahRes.data.name);
      setCurrentSurah(surahRes.data);
      
      // Sort verses by order field to ensure correct sequence
      const sortedVerses = (versesRes.data || []).sort((a, b) => {
        return (a.order || 0) - (b.order || 0);
      });
      setCurrentVerses(sortedVerses);
      const newRecitations = recitationsRes.data || [];
      setRecitations(newRecitations);

      // Check for RECITER ID in URL (read fresh from searchParams)
      const urlReciterId = searchParams.get('reciter');
      const urlRecitationId = searchParams.get('recitation');
      
      // Auto-select recitation from URL if RECITER ID or RECITATION ID provided
      if ((urlReciterId || urlRecitationId) && newRecitations.length > 0) {
        let targetRecitation = null;
        
        // First try to find by recitation ID (from SurahList "Dinle" button)
        if (urlRecitationId) {
          targetRecitation = newRecitations.find(r => 
            r.id === urlRecitationId || r._id === urlRecitationId
          );
          console.log('[QuranReader] Looking for recitation ID:', urlRecitationId);
        }
        
        // If not found, try by reciter ID
        if (!targetRecitation && urlReciterId) {
          targetRecitation = newRecitations.find(r => 
            r.reciterId === urlReciterId || r.reciter?.id === urlReciterId
          );
          console.log('[QuranReader] Looking for reciter ID:', urlReciterId);
        }
        
        if (targetRecitation) {
          console.log('[QuranReader] ✅ Found recitation from URL, auto-starting:', targetRecitation.reciter?.name);
          setSelectedRecitation(targetRecitation);
          setIsPlaying(true);
          setHighlightedVerseIndex(null); // Reset highlight
        } else {
          // Reciter not available for this surah - show modal
          console.log('[QuranReader] ❌ Recitation/Reciter from URL not available in this surah');
          setIsPlaying(false);
          setSelectedRecitation(null);
          setHighlightedVerseIndex(null);
          
          // Show modal for user to select another reciter
          setReciterChangeAlert({
            oldReciter: 'Seçili hafız',
            surahName: surahRes.data.name,
            availableReciters: newRecitations,
            needsSelection: true
          });
        }
      } else {
        // No URL recitation - check if we should auto-select first
        if (autoplayFromUrl && newRecitations.length > 0) {
          // Autoplay enabled - use first recitation and start playing
          console.log('[QuranReader] Autoplay enabled - selecting first recitation and starting playback');
          setSelectedRecitation(newRecitations[0]);
          setIsPlaying(true);
          setHighlightedVerseIndex(null);
        } else if (newRecitations.length > 0 && selectedRecitation) {
          // User was listening, try to continue with SAME RECITER in new surah
          console.log('[QuranReader] Trying to continue with same reciter:', selectedRecitation.reciter?.name);
          console.log('[QuranReader] Current reciter ID:', selectedRecitation.reciterId);
          console.log('[QuranReader] Available recitations:', newRecitations.map(r => ({ id: r.reciterId, name: r.reciter?.name })));
          
          // Find recitation from same reciter (try multiple fields for compatibility)
          const sameReciterRecitation = newRecitations.find(r => 
            r.reciterId === selectedRecitation.reciterId || 
            r.reciter?.id === selectedRecitation.reciter?.id
          );
          
          if (sameReciterRecitation) {
            console.log('[QuranReader] ✅ Found same reciter, continuing playback');
            setSelectedRecitation(sameReciterRecitation);
            setIsPlaying(true);
            setHighlightedVerseIndex(null);
          } else {
            // Same reciter not available for this surah
            console.log('[QuranReader] ❌ Same reciter not available in this surah');
            
            // PAUSE playback and show modal for user selection
            setIsPlaying(false);
            setSelectedRecitation(null);
            setHighlightedVerseIndex(null);
            
            // Show modal with reciter selection
            setReciterChangeAlert({
              oldReciter: selectedRecitation.reciter?.name || 'Önceki hafız',
              surahName: surahRes.data.name,
              availableReciters: newRecitations,
              needsSelection: true
            });
          }
        } else {
          // STOP audio when surah changes
          setSelectedRecitation(null);
          setIsPlaying(false);
          setHighlightedVerseIndex(null);
        }
      }
    } catch (error) {
      console.error('[QuranReader] Error loading surah data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenReciterDialog = () => {
    setIsReciterDialogOpen(true);
  };

  const handleSelectRecitation = async (recitation) => {
    setSelectedRecitation(recitation);
    setIsPlaying(true);
    setIsReciterDialogOpen(false);
    
    // Increment listen count for the surah
    if (currentSurah && currentSurah.id) {
      try {
        await fetch(`${BACKEND_URL}/api/surahs/${currentSurah.id}/listen`, {
          method: 'POST'
        });
        console.log('[Listen Count] Incremented for:', currentSurah.name);
      } catch (error) {
        console.error('[Listen Count] Error:', error);
      }
    }
  };

  // Audio time update handler - Calculate which verse is being recited
  // Uses DYNAMIC timing based on verse length (character count)
  const handleAudioTimeUpdate = (currentTime, actualDuration) => {
    setCurrentAudioTime(currentTime);
    
    if (currentVerses.length === 0 || !selectedRecitation) return;
    
    // Use ACTUAL audio duration from the audio element, NOT database duration!
    // Database duration may be incorrect or formatted differently
    const duration = actualDuration || parseDuration(selectedRecitation.duration);
    if (duration === 0) return;
    
    // Calculate verse timings based on character count
    const verseTimings = calculateVerseTimings(currentVerses, duration);
    
    // Find which verse is currently being recited
    const verseIndex = findCurrentVerseIndex(currentTime, verseTimings);
    
    // Update highlight if verse changed
    if (verseIndex >= 0 && verseIndex < currentVerses.length && verseIndex !== highlightedVerseIndex) {
      setHighlightedVerseIndex(verseIndex);
      scrollToVerse(verseIndex);
      console.log(`[Audio Sync] Highlighting verse ${verseIndex + 1}/${currentVerses.length} at ${currentTime.toFixed(1)}s (total: ${duration.toFixed(0)}s)`);
    }
  };

  // Handle audio ended - Show next surah modal
  const handleAudioEnded = () => {
    console.log('[Audio] Playback ended');
    
    // Find current surah index
    const currentIndex = surahs.findIndex(s => s.id === currentSurah?.id);
    if (currentIndex === -1 || currentIndex >= surahs.length - 1) {
      console.log('[Auto Next] Last surah, no next surah available');
      return;
    }

    // Get next surah
    const nextSurah = surahs[currentIndex + 1];
    setNextSurahInfo(nextSurah);
    setShowNextSurahModal(true);
    setNextSurahCountdown(7);
  };

  // Navigate to next surah
  const handleNavigateToNextSurah = (nextSurah) => {
    console.log('[Auto Next] Navigating to:', nextSurah.name);
    
    // Store current RECITER ID (not recitation ID) before navigation
    const currentReciterId = selectedRecitation?.reciterId || selectedRecitation?.reciter?.id;
    
    // Close modal
    setShowNextSurahModal(false);
    setHighlightedVerseIndex(null);
    
    // Navigate to next surah with autoplay and RECITER ID
    navigate(`/?surah=${nextSurah.slug || nextSurah.id}&autoplay=true&reciter=${currentReciterId}`);
  };

  // Cancel auto next
  const handleCancelAutoNext = () => {
    console.log('[Auto Next] Cancelled by user');
    setShowNextSurahModal(false);
    setNextSurahCountdown(7);
    setNextSurahInfo(null);
  };

  // Countdown effect for auto-next modal
  useEffect(() => {
    if (showNextSurahModal && nextSurahCountdown > 0) {
      const timer = setTimeout(() => {
        setNextSurahCountdown(prev => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (showNextSurahModal && nextSurahCountdown === 0 && nextSurahInfo) {
      // Navigate to next surah when countdown reaches 0
      handleNavigateToNextSurah(nextSurahInfo);
    }
  }, [showNextSurahModal, nextSurahCountdown, nextSurahInfo]);

  // No cleanup needed - using setTimeout in useEffect above

  // Calculate timing for each verse based on character count
  const calculateVerseTimings = (verses, totalDuration) => {
    if (!verses || verses.length === 0) return [];
    
    // Count characters in each verse (Arabic text is more accurate)
    const verseLengths = verses.map(v => {
      const arabicLength = (v.textArabic || '').trim().length;
      const turkishLength = (v.textTurkish || '').trim().length;
      // Use Arabic length primarily, fallback to Turkish
      return arabicLength > 0 ? arabicLength : turkishLength;
    });
    
    // Calculate total characters
    const totalChars = verseLengths.reduce((sum, len) => sum + len, 0);
    const avgCharsPerVerse = totalChars / verses.length;
    
    if (totalChars === 0 || avgCharsPerVerse === 0) {
      // Fallback to equal distribution
      const timePerVerse = totalDuration / verses.length;
      return verses.map((_, i) => ({
        startTime: i * timePerVerse,
        endTime: (i + 1) * timePerVerse
      }));
    }
    
    // Adaptive weighting based on surah length and duration
    const isShortSurah = verses.length < 10;
    const avgTimePerVerse = totalDuration / verses.length;
    
    // If each verse has very short time (< 1 second on average), use more equal distribution
    const useMoreEqual = avgTimePerVerse < 1.0;
    
    let equalWeight, charWeight;
    if (isShortSurah) {
      // Short surahs: 40% equal, 60% char-based
      equalWeight = 0.4;
      charWeight = 0.6;
    } else if (useMoreEqual) {
      // Fast recitations: 30% equal, 70% char-based
      equalWeight = 0.3;
      charWeight = 0.7;
    } else {
      // Normal/long recitations: 10% equal, 90% char-based
      equalWeight = 0.1;
      charWeight = 0.9;
    }
    
    // Calculate time allocation for each verse
    const timePerVerse = totalDuration / verses.length;
    let cumulativeTime = 0;
    
    const timings = verses.map((verse, index) => {
      // Hybrid calculation: weighted average of character-based and equal distribution
      const charRatio = verseLengths[index] / totalChars;
      const charBasedDuration = totalDuration * charRatio;
      const verseDuration = (charBasedDuration * charWeight) + (timePerVerse * equalWeight);
      
      const startTime = cumulativeTime;
      const endTime = cumulativeTime + verseDuration;
      cumulativeTime = endTime;
      
      return { startTime, endTime, duration: verseDuration };
    });
    
    // Debug log on first calculation
    if (verses.length > 0) {
      console.log(`[Timing] Surah: ${verses.length} verses, ${totalDuration.toFixed(1)}s duration (${avgTimePerVerse.toFixed(2)}s/verse)`);
      console.log(`[Timing] Weights: ${(charWeight * 100).toFixed(0)}% char-based + ${(equalWeight * 100).toFixed(0)}% equal`);
    }
    
    return timings;
  };

  // Find which verse is currently playing
  const findCurrentVerseIndex = (currentTime, verseTimings) => {
    for (let i = 0; i < verseTimings.length; i++) {
      if (currentTime >= verseTimings[i].startTime && currentTime < verseTimings[i].endTime) {
        return i;
      }
    }
    // If we're past the last verse, highlight the last one
    if (currentTime >= verseTimings[verseTimings.length - 1].endTime) {
      return verseTimings.length - 1;
    }
    return 0;
  };

  // Parse duration string (e.g., "06:26" or "02:26:54") to seconds
  const parseDuration = (duration) => {
    if (!duration) return 0;
    const parts = duration.split(':').map(Number);
    if (parts.length === 2) {
      // MM:SS format
      return parts[0] * 60 + parts[1];
    } else if (parts.length === 3) {
      // HH:MM:SS format
      return parts[0] * 3600 + parts[1] * 60 + parts[2];
    }
    return 0;
  };

  // Scroll to verse smoothly
  const scrollToVerse = (verseIndex) => {
    const verseElements = document.querySelectorAll(`[data-verse-index="${verseIndex}"]`);
    if (verseElements.length > 0) {
      verseElements[0].scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  };

  // Memoize currentTrack - ONLY based on recitation, NOT surah name
  const currentTrack = React.useMemo(() => {
    if (!selectedRecitation) return null;
    return {
      audioUrl: selectedRecitation.audioUrl,
      surahName: selectedRecitation.surahName || currentSurah?.name || '',
      reciterName: selectedRecitation.reciter?.name || '',
      reciterImage: selectedRecitation.reciter?.image || '',
      id: selectedRecitation.id
    };
  }, [
    selectedRecitation?.audioUrl,
    selectedRecitation?.id,
    selectedRecitation?.reciter?.name,
    selectedRecitation?.reciter?.image,
    selectedRecitation?.surahName
    // NOT INCLUDING currentSurah.name - this changes between surahs!
  ]);

  const handlePreviousSurah = () => {
    if (!currentSurah || surahs.length === 0) return;
    const currentIndex = surahs.findIndex(s => s.id === currentSurah.id);
    if (currentIndex > 0) {
      const newSurah = surahs[currentIndex - 1];
      const identifier = newSurah.slug || newSurah.id;
      console.log('[QuranReader] Previous surah:', newSurah.name, '→', identifier);
      
      // If audio is playing or recitation is selected, continue with autoplay using RECITER ID
      if (selectedRecitation) {
        const reciterId = selectedRecitation.reciterId || selectedRecitation.reciter?.id;
        navigate(`/?surah=${identifier}&autoplay=true&reciter=${reciterId}`);
      } else {
        navigate(`/?surah=${identifier}`);
      }
    }
  };

  const handleNextSurah = () => {
    if (!currentSurah || surahs.length === 0) return;
    const currentIndex = surahs.findIndex(s => s.id === currentSurah.id);
    if (currentIndex < surahs.length - 1) {
      const newSurah = surahs[currentIndex + 1];
      const identifier = newSurah.slug || newSurah.id;
      console.log('[QuranReader] Next surah:', newSurah.name, '→', identifier);
      
      // If audio is playing or recitation is selected, continue with autoplay using RECITER ID
      if (selectedRecitation) {
        const reciterId = selectedRecitation.reciterId || selectedRecitation.reciter?.id;
        navigate(`/?surah=${identifier}&autoplay=true&reciter=${reciterId}`);
      } else {
        navigate(`/?surah=${identifier}`);
      }
    }
  };

  if (loading || !currentSurah) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#F9F6F1]">
        <p className="text-[#8B7355]">{getText('reader.loading', 'Yükleniyor...')}</p>
      </div>
    );
  }

  // Dynamic SEO for current surah
  const pageTitle = currentSurah 
    ? `${currentSurah.name} - ${currentSurah.nameArabic} | Kuran-ı Kerim` 
    : 'Kuran-ı Kerim - Yüce Kuran\'ı Okuyun ve Dinleyin';
  
  const pageDescription = currentSurah
    ? `${currentSurah.name} suresi (${currentSurah.nameArabic}), ${currentSurah.versesCount} ayet. Kuran-ı Kerim'i okuyun ve dinleyin.`
    : 'Kuran-ı Kerim surelerini okuyun, dinleyin ve anlayın. Farklı mealler ve hafızlarla Kuran-ı Kerim\'e ulaşın.';

  return (
    <>
      <SEOHead 
        title={pageTitle}
        description={pageDescription}
        keywords={`${currentSurah?.name || 'kuran'}, kuran-ı kerim, meal, ayet, sure, islam`}
        ogTitle={pageTitle}
        ogDescription={pageDescription}
      />
      
      <div className="min-h-screen bg-[#F9F6F1] pb-32">
        {/* Pattern Background */}
        <div className="fixed inset-0 opacity-5 pointer-events-none" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 0l10 10-10 10-10-10zM30 40l10 10-10 10-10-10z' fill='%238B7355' fill-opacity='1' fill-rule='evenodd'/%3E%3C/svg%3E")`,
          backgroundSize: '60px 60px'
        }} />

      {/* Header Controls */}
      <div className="sticky top-14 sm:top-16 z-10 bg-[#F5EFE7] border-b-2 border-[#D5C4A1] shadow-sm">
        <div className="w-full px-3 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              onClick={handlePreviousSurah}
              disabled={surahs.findIndex(s => s.id === currentSurah.id) === 0}
              className="text-[#6B5744] hover:bg-[#E8DCC8] text-xs sm:text-sm px-2 sm:px-4"
            >
              <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5 sm:mr-1" />
              <span className="hidden sm:inline">{getText('reader.previous_surah', 'Önceki Sure')}</span>
            </Button>

            <div className="text-center flex-1 px-2">
              <h1 className="text-lg sm:text-2xl font-bold text-[#5C4033]">{currentSurah.name}</h1>
              <p className="text-xs sm:text-sm text-[#8B7355] mt-1">
                {currentSurah.nameArabic} • {currentSurah.verses} {getText('reader.verse_label', 'Ayet')}
              </p>
              <p className="text-xs text-[#8B7355] hidden sm:block">
                {currentSurah.revelationType}
              </p>
              {getText('homepage.quran_title') && (
                <div className="mt-2 sm:mt-4 space-y-1 hidden md:block">
                  <p className="text-base sm:text-xl text-[#5C4033] font-bold">{getText('homepage.quran_title')}</p>
                  {getText('homepage.approach_title') && <p className="text-xs sm:text-sm text-[#8B7355]">{getText('homepage.approach_title')}</p>}
                  {getText('homepage.meaning_title') && <p className="text-xs sm:text-sm text-[#8B7355]">{getText('homepage.meaning_title')}</p>}
                  {getText('homepage.prepared_by') && <p className="text-xs text-[#8B7355] mt-2">{getText('homepage.prepared_by')}</p>}
                  {getText('homepage.commented_by') && <p className="text-xs text-[#8B7355]">{getText('homepage.commented_by')}</p>}
                </div>
              )}
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={handleNextSurah}
              disabled={surahs.findIndex(s => s.id === currentSurah.id) === surahs.length - 1}
              className="text-[#6B5744] hover:bg-[#E8DCC8] text-xs sm:text-sm px-2 sm:px-4"
            >
              <span className="hidden sm:inline">{getText('reader.next_surah', 'Sonraki Sure')}</span>
              <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5 sm:ml-1" />
            </Button>
          </div>

          {/* View Options - Toggle Buttons */}
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mt-3 sm:mt-4">
            {/* Türkçe Meal Toggle */}
            <button
              onClick={() => setShowTurkish(!showTurkish)}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full transition-all duration-300 text-xs font-medium border ${
                showTurkish
                  ? 'bg-white text-[#8B7355] border-[#8B7355] shadow-sm'
                  : 'bg-[#8B7355] text-white border-[#8B7355] hover:bg-[#6B5744]'
              }`}
            >
              <span>{getText('reader.turkish_meal', 'Türkçe Meal')}</span>
              <span className="relative flex items-center">
                <span className={`w-7 h-3.5 rounded-full transition-all duration-300 ${
                  showTurkish ? 'bg-[#8B7355]/20' : 'bg-white/30'
                }`}>
                  <span className={`absolute top-0.5 w-2.5 h-2.5 rounded-full shadow-sm transition-all duration-300 ${
                    showTurkish ? 'right-0.5 bg-[#8B7355]' : 'left-0.5 bg-white'
                  }`}></span>
                </span>
              </span>
            </button>

            {/* Arapça Metin Toggle */}
            <button
              onClick={() => setShowArabic(!showArabic)}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full transition-all duration-300 text-xs font-medium border ${
                showArabic
                  ? 'bg-white text-[#8B7355] border-[#8B7355] shadow-sm'
                  : 'bg-[#8B7355] text-white border-[#8B7355] hover:bg-[#6B5744]'
              }`}
            >
              <span>{getText('reader.arabic_text', 'Arapça Metin')}</span>
              <span className="relative flex items-center">
                <span className={`w-7 h-3.5 rounded-full transition-all duration-300 ${
                  showArabic ? 'bg-[#8B7355]/20' : 'bg-white/30'
                }`}>
                  <span className={`absolute top-0.5 w-2.5 h-2.5 rounded-full shadow-sm transition-all duration-300 ${
                    showArabic ? 'right-0.5 bg-[#8B7355]' : 'left-0.5 bg-white'
                  }`}></span>
                </span>
              </span>
            </button>

            {/* Sure Dinle Button */}
            <Button 
              variant="outline" 
              size="sm" 
              className="text-[#8B7355] border-[#D5C4A1] hover:bg-[#F5EFE7] text-xs flex items-center gap-1.5 px-2.5 py-1.5 h-auto"
              onClick={handleOpenReciterDialog}
            >
              <Volume2 className="h-3 w-3" />
              <span className="inline">{getText('reader.listen_surah', 'Dinle')}</span>
              <span className="w-2 h-2 bg-red-500 rounded-full inline-block" style={{
                animation: 'live-pulse 1.5s ease-in-out infinite'
              }}></span>
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content - Full Width Layout */}
      <div className="w-full px-3 sm:px-6 lg:px-8 py-4 sm:py-8 relative">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-6 lg:gap-8">
          {/* Arabic Text Column - Now on the RIGHT (second in order) */}
          {showArabic && (
            <div className="space-y-3 sm:space-y-6 lg:order-2">
              <Card className="bg-[#FDFBF7] border-2 border-[#E8DCC8] p-3 sm:p-6">
                <div className="space-y-4 sm:space-y-6">
                  {/* Sure Adı - Arapça */}
                  <div className="text-center mb-4">
                    <h3 className="text-base sm:text-lg font-bold text-[#5C4033] mb-2" dir="rtl" style={{ fontFamily: '"Traditional Arabic", "Scheherazade New", serif' }}>
                      {currentSurah.nameArabic}
                    </h3>
                    
                    {/* Bismillah - Sadece aktif ise göster */}
                    {currentSurah?.showBismillah === true && (
                      <p className="text-2xl sm:text-4xl text-[#5C4033] leading-loose" dir="rtl" style={{ fontFamily: '"Traditional Arabic", "Scheherazade New", serif' }}>
                        بِسْمِ اللّٰهِ الرَّحْمٰنِ الرَّحِيمِ
                      </p>
                    )}
                  </div>
                  
                  {currentVerses.map((verse, index) => (
                    <div 
                      key={verse.id} 
                      data-verse-index={index}
                      className={`group text-right p-3 rounded-lg transition-all duration-300 ${
                        highlightedVerseIndex === index 
                          ? 'bg-[#FFF4E6] ring-2 ring-[#D4A574] shadow-md' 
                          : 'hover:bg-[#FDFBF7]'
                      }`}
                    >
                      <p className="text-lg sm:text-2xl text-[#3E3028] leading-loose flex items-center justify-between" dir="rtl" style={{ fontFamily: '"Traditional Arabic", "Scheherazade New", serif' }}>
                        <span className={`inline-flex w-7 h-7 sm:w-8 sm:h-8 rounded-full text-xs sm:text-sm items-center justify-center font-semibold transition-colors flex-shrink-0 order-first ${
                          highlightedVerseIndex === index
                            ? 'bg-[#D4A574] text-white'
                            : 'bg-[#E8DCC8] text-[#5C4033]'
                        }`}>
                          {verse.verseNumber}
                        </span>
                        <span className="flex-1 pr-3">{verse.textArabic}</span>
                      </p>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          )}

          {/* Turkish Translation Column - Now on the LEFT (first in order) */}
          {showTurkish && (
            <div className="space-y-3 sm:space-y-6 lg:order-1">
              <Card className="bg-[#FDFBF7] border-2 border-[#E8DCC8] p-3 sm:p-6">
                <div className="space-y-3 sm:space-y-4">
                  {/* Sure Adı - Türkçe */}
                  <div className="text-center mb-4">
                    <h3 className="text-base sm:text-lg font-bold text-[#5C4033] mb-2">
                      {currentSurah.name}
                    </h3>
                    
                    {/* Bismillah - Sadece aktif ise göster */}
                    {currentSurah?.showBismillah === true && (
                      <p className="text-lg sm:text-xl font-semibold text-[#5C4033]">
                        Bismillahirrahmanirrahim
                      </p>
                    )}
                  </div>
                  
                  {currentVerses.map((verse, index) => (
                    <div 
                      key={verse.id} 
                      data-verse-index={index}
                      className={`group p-3 rounded-lg transition-all duration-300 ${
                        highlightedVerseIndex === index 
                          ? 'bg-[#FFF4E6] ring-2 ring-[#D4A574] shadow-md' 
                          : 'hover:bg-[#FDFBF7]'
                      }`}
                    >
                      <div className="flex items-start space-x-2 sm:space-x-3">
                        <div className={`flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center font-semibold text-xs sm:text-sm transition-colors ${
                          highlightedVerseIndex === index
                            ? 'bg-[#D4A574] text-white'
                            : 'bg-[#D5C4A1] text-[#5C4033]'
                        }`}>
                          {verse.verseNumber}
                        </div>
                        <p className="flex-1 text-[#3E3028] leading-relaxed text-sm sm:text-base">
                          {verse.textTurkish}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>

      {/* Reciter Change Alert Modal - YENI: Kullanıcı Seçimi */}
      <Dialog open={!!reciterChangeAlert} onOpenChange={() => setReciterChangeAlert(null)}>
        <DialogContent className="sm:max-w-[500px] bg-[#F9F6F1] border-[#D5C4A1] max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-[#5C4033] text-center">
              ⚠️ Hafız Ses Kaydı Bulunamadı
            </DialogTitle>
          </DialogHeader>
          
          {reciterChangeAlert && (
            <div className="py-4 space-y-4">
              {/* Uyarı Mesajı */}
              <div className="text-center space-y-2 bg-orange-50 p-4 rounded-lg border border-orange-200">
                <p className="text-[#5C4033] font-medium">
                  <span className="font-bold text-orange-600">{reciterChangeAlert.oldReciter}</span> hafızın
                </p>
                <p className="text-[#5C4033] font-medium">
                  <span className="font-bold">{reciterChangeAlert.surahName}</span> suresi için ses kaydı bulunamadı.
                </p>
              </div>

              {/* Hafız Seçim Listesi */}
              {reciterChangeAlert.needsSelection && reciterChangeAlert.availableReciters && (
                <div className="space-y-3">
                  <p className="text-center text-[#8B7355] font-semibold">
                    Hangi hafızla devam etmek istersiniz?
                  </p>
                  
                  <div className="grid gap-2 max-h-[300px] overflow-y-auto">
                    {reciterChangeAlert.availableReciters.map((recitation) => (
                      <button
                        key={recitation.id}
                        onClick={() => {
                          console.log('[ReciterSelect] User selected:', recitation.reciter?.name);
                          setSelectedRecitation(recitation);
                          setIsPlaying(true);
                          setHighlightedVerseIndex(null);
                          setReciterChangeAlert(null);
                        }}
                        className="flex items-center gap-3 p-3 bg-white hover:bg-[#E8DCC8] rounded-lg border-2 border-[#D5C4A1] hover:border-[#8B7355] transition-all group"
                      >
                        {/* Hafız Resmi */}
                        <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-[#8B7355] flex-shrink-0">
                          <img 
                            src={recitation.reciter?.image ? 
                              (recitation.reciter.image.startsWith('http') ? recitation.reciter.image : 
                              `${process.env.REACT_APP_BACKEND_URL || ''}/api/files/${recitation.reciter.image.replace('/uploads/', '')}`) 
                              : ''
                            }
                            alt={recitation.reciter?.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        
                        {/* Hafız Bilgisi */}
                        <div className="flex-1 text-left">
                          <p className="font-semibold text-[#5C4033] group-hover:text-[#8B7355]">
                            {recitation.reciter?.name}
                          </p>
                          <p className="text-xs text-[#8B7355]">
                            {recitation.reciter?.country || 'Hafız'}
                          </p>
                        </div>
                        
                        {/* Ok İkonu */}
                        <div className="text-[#8B7355] opacity-0 group-hover:opacity-100 transition-opacity">
                          ▶
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* İptal Butonu */}
              <div className="flex justify-center pt-2">
                <Button
                  onClick={() => {
                    setReciterChangeAlert(null);
                    setIsPlaying(false);
                  }}
                  variant="outline"
                  className="border-[#8B7355] text-[#8B7355] hover:bg-[#8B7355] hover:text-white"
                >
                  İptal (Durdur)
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Auto Next Surah Modal */}
      {showNextSurahModal && nextSurahInfo && (
        <div className="fixed inset-0 z-50 flex items-end justify-center p-4 pointer-events-none">
          <div className="bg-gradient-to-r from-[#8B7355] to-[#6B5744] rounded-2xl shadow-2xl p-6 max-w-md w-full pointer-events-auto animate-slide-up border-2 border-[#D5C4A1]">
            {/* Progress Bar */}
            <div className="w-full h-2 bg-[#5C4033] rounded-full mb-4 overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-green-400 to-green-600 transition-all duration-1000 ease-linear"
                style={{ width: `${(nextSurahCountdown / 7) * 100}%` }}
              />
            </div>

            {/* Content */}
            <div className="text-center text-white space-y-3">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Volume2 className="h-6 w-6" />
                <h3 className="text-xl font-bold">Sonraki Sureye Geçiliyor</h3>
              </div>
              
              <div className="bg-white/10 rounded-lg p-4 backdrop-blur-sm">
                <p className="text-lg font-semibold mb-1">{nextSurahInfo.name}</p>
                <p className="text-sm opacity-90">{nextSurahInfo.nameArabic}</p>
              </div>

              {/* Countdown */}
              <div className="flex items-center justify-center gap-2 py-3">
                <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center border-2 border-white/40">
                  <span className="text-3xl font-bold">{nextSurahCountdown}</span>
                </div>
                <span className="text-sm opacity-90">saniye sonra başlıyor</span>
              </div>

              {/* Cancel Button */}
              <button
                onClick={handleCancelAutoNext}
                className="w-full py-3 px-4 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-lg font-medium transition-all hover:scale-105 border border-white/30"
              >
                İptal Et
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reciter Selection Dialog */}
      <Dialog open={isReciterDialogOpen} onOpenChange={setIsReciterDialogOpen}>
        <DialogContent className="max-w-xs sm:max-w-md md:max-w-2xl bg-[#F9F6F1] max-h-[90vh] overflow-y-auto">
          <style dangerouslySetInnerHTML={{__html: `
            [role="dialog"] > button[type="button"]:first-of-type { display: none !important; }
          `}} />
          <div className="flex justify-between items-center mb-3 sm:mb-4">
            <h2 className="text-lg sm:text-xl font-bold text-[#5C4033]">{getText('reader.select_reciter', 'Hafız Seçin')}</h2>
            <button
              onClick={() => setIsReciterDialogOpen(false)}
              className="flex items-center gap-1.5 px-2 sm:px-2.5 py-1.5 rounded-md bg-white hover:bg-[#E8DCC8] border border-[#D5C4A1] hover:border-[#8B7355] transition-all shadow-sm hover:shadow-md flex-shrink-0"
            >
              <X className="h-4 w-4 text-[#8B7355]" />
              <span className="text-[#8B7355] font-medium text-xs">{getText('reader.close', 'Kapat')}</span>
            </button>
          </div>
          
          {recitations.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-[#8B7355]">
                {getText('reader.no_reciter', 'Bu sure için henüz hafız kaydedilmemiş.')}
              </p>
            </div>
          ) : (
            <div className="space-y-2 sm:space-y-3 max-h-96 overflow-y-auto">
              {recitations.map((recitation) => (
                <Card
                  key={recitation.id}
                  className="bg-white border-[#E8DCC8] p-3 sm:p-4 hover:bg-[#F5EFE7] hover:border-[#D5C4A1] transition-all"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center space-x-2 sm:space-x-4 flex-1 min-w-0">
                      {/* Reciter Profile Image */}
                      <div className="relative flex-shrink-0">
                        {recitation.reciter?.image || recitation.reciter?.imageUrl ? (
                          <img
                            src={getFileUrl(recitation.reciter?.image || recitation.reciter?.imageUrl)}
                            alt={recitation.reciter?.name || 'Hafız'}
                            className="w-12 h-12 sm:w-16 sm:h-16 rounded-full object-cover border-2 border-[#D5C4A1]"
                            onError={(e) => {
                              console.error('[Image Error]', recitation.reciter?.name, '- Failed to load:', e.target.src);
                              e.target.style.display = 'none';
                              e.target.nextElementSibling.style.display = 'flex';
                            }}
                            onLoad={() => {
                              console.log('[Image Success]', recitation.reciter?.name, 'loaded successfully');
                            }}
                          />
                        ) : null}
                        {/* Fallback avatar */}
                        <div 
                          className={`w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-gradient-to-br from-[#D5C4A1] to-[#8B7355] text-white text-xl sm:text-2xl font-bold flex items-center justify-center border-2 border-[#8B7355] shadow-md ${
                            (recitation.reciter?.image || recitation.reciter?.imageUrl) ? 'hidden' : 'flex'
                          }`}
                        >
                          {(recitation.reciter?.name || 'H')[0].toUpperCase()}
                        </div>
                      </div>
                      
                      <div className="flex-1">
                        <h3 className="text-base sm:text-lg font-bold text-[#5C4033]">
                          {recitation.reciter?.name || 'Bilinmeyen Hafız'}
                        </h3>
                        <div className="flex items-center gap-2 text-xs sm:text-sm text-[#8B7355] mt-1 flex-wrap">
                          {/* Bayrak ve ülke adı sadece desktop'ta görünür */}
                          {recitation.reciter?.flagUrl && (
                            <img 
                              src={recitation.reciter.flagUrl.startsWith('http') ? recitation.reciter.flagUrl : `${process.env.REACT_APP_BACKEND_URL}${recitation.reciter.flagUrl.replace('/uploads/', '/api/files/')}`} 
                              alt={`${recitation.reciter.country} Bayrağı`} 
                              className="w-8 h-6 object-cover rounded hidden sm:block"
                            />
                          )}
                          <span className="hidden sm:inline">{recitation.reciter?.country || ''}</span>
                          <span className="hidden sm:inline">•</span>
                          <span className="font-medium">{currentSurah?.name || 'Sure'}</span>
                          <span>•</span>
                          <span>{recitation.duration}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Listen Button */}
                    <Button
                      onClick={() => handleSelectRecitation(recitation)}
                      size="sm"
                      className="bg-[#8B7355] hover:bg-[#6B5744] text-white flex items-center gap-1 sm:gap-2 flex-shrink-0 text-xs sm:text-sm"
                    >
                      <Volume2 className="h-4 w-4 sm:h-5 sm:w-5" />
                      {getText('button.listen', 'Dinle')}
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Compact Audio Player Component */}
      {selectedRecitation && currentTrack && (
        <CompactAudioPlayer
          key={`${selectedRecitation.id}-${currentSurah?.id}`}
          reciterImage={currentTrack.reciterImage}
          reciterName={currentTrack.reciterName}
          surahName={currentTrack.surahName}
          audioUrl={currentTrack.audioUrl}
          trackDuration={selectedRecitation?.duration}
          currentReciterId={selectedRecitation.id}
          allReciters={recitations.map(r => ({ id: r.id, name: r.reciter?.name, image: r.reciter?.image }))}
          onTimeUpdate={handleAudioTimeUpdate}
          onEnded={handleAudioEnded}
          onChangeReciter={() => setIsReciterDialogOpen(true)}
          onClose={() => {
            setIsPlaying(false);
            setSelectedRecitation(null);
            setHighlightedVerseIndex(null);
          }}
        />
      )}
      </div>
    </>
  );
};

export default QuranReader;